import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Axios from '@/shared/api/axios';

const WithdrawModal = forwardRef(({ onConfirm, onError, balance }, ref) => {
  const { t } = useTranslation();
  const currentIdolId = useSelector(state => state.user.idolId);

  const [show, setShow] = useState(false);
  const [formData, setFormData] = useState({
    walletAddress: '',
    amount: ''
  });
  const [errors, setErrors] = useState({
    walletAddress: '',
    amount: ''
  });

  // 提領設定（從 API 取得）。不給預設值：手續費率／門檻取不到就不讓使用者送出
  const [settings, setSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const settingsReady = !loadingSettings && settings !== null;

  // 取得提領設定
  useEffect(() => {
    const fetchSettings = async () => {
      if (!show || !currentIdolId) return;

      setLoadingSettings(true);
      try {
        const response = await Axios.get('/wallet/withdrawal-settings', {
          params: { idol_id: currentIdolId }
        });

        if (response.data?.status_code === 'SYSTEM_1000' && response.data.data) {
          const data = response.data.data;
          setSettings({
            minWithdrawalAmount: parseFloat(data.min_withdrawal_amount),
            withdrawalFeeRate: parseFloat(data.withdrawal_fee_rate),
            withdrawalFeeFixed: parseFloat(data.withdrawal_fee_fixed),
            withdrawalProcessingHours: parseInt(data.withdrawal_processing_hours)
          });
        } else if (response.data?.status_code) {
          onError(response.data.status_code);
          handleClose();
        }
      } catch (error) {
        console.error('Failed to fetch withdrawal settings:', error);
        // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
        if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
          onError(error.httpErrorCode);
          handleClose();
        }
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchSettings();
  }, [show, currentIdolId, onError]);

  useImperativeHandle(ref, () => ({
    show: () => {
      setFormData({
        walletAddress: '',
        amount: ''
      });
      setErrors({
        walletAddress: '',
        amount: ''
      });
      setSettings(null);
      setShow(true);
    },
    hide: () => {
      setShow(false);
    }
  }));

  const handleClose = () => {
    setShow(false);
    setFormData({
      walletAddress: '',
      amount: ''
    });
    setErrors({
      walletAddress: '',
      amount: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    const isValidErc20Address = (address) => {
      const trimmed = address.trim();
      // ERC20 (EVM) 通用地址規則：0x 開頭 + 40 位 16 進位字元（不檢查 checksum）
      const erc20Regex = /^0x[a-fA-F0-9]{40}$/;
      return erc20Regex.test(trimmed);
    };

    // 驗證邏輯
    if (field === 'amount') {
      const amount = parseFloat(value);
      if (!value || value === '') {
        setErrors(prev => ({ ...prev, amount: '' }));
      } else if (isNaN(amount) || amount <= 0) {
        setErrors(prev => ({ ...prev, amount: t('invalid_amount') }));
      } else if (amount < settings.minWithdrawalAmount) {
        setErrors(prev => ({ ...prev, amount: t('minimum_withdraw_error', { min: settings.minWithdrawalAmount }) }));
      } else if (amount > balance) {
        setErrors(prev => ({ ...prev, amount: t('insufficient_balance') }));
      } else {
        setErrors(prev => ({ ...prev, amount: '' }));
      }
    }

    if (field === 'walletAddress') {
      if (!value || value.trim() === '') {
        setErrors(prev => ({ ...prev, walletAddress: '' }));
      } else if (!isValidErc20Address(value)) {
        setErrors(prev => ({ ...prev, walletAddress: t('invalid_wallet_address') }));
      } else {
        setErrors(prev => ({ ...prev, walletAddress: '' }));
      }
    }
  };

  // 計算手續費（取比率和固定費用的較大值）
  const calculateFee = () => {
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) return 0;
    const feeByRate = amount * settings.withdrawalFeeRate / 100;
    const feeByFixed = settings.withdrawalFeeFixed;
    return Math.max(feeByRate, feeByFixed);
  };

  // 計算實際到帳金額
  const calculateActualAmount = () => {
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) return 0;
    return amount - calculateFee();
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm({
        ...formData,
        actualAmount: calculateActualAmount(),
        fee: calculateFee(),
        processingHours: settings.withdrawalProcessingHours
      });
    }
    handleClose();
  };

  const isValid = () => {
    if (!settingsReady) return false;
    const amount = parseFloat(formData.amount);
    return (
      formData.walletAddress.trim() &&
      !isNaN(amount) &&
      amount >= settings.minWithdrawalAmount &&
      amount <= balance &&
      !errors.amount &&
      !errors.walletAddress
    );
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header className="border-bottom pb-3" closeButton>
        <Modal.Title className="fw-bold">{t('withdraw')}</Modal.Title>
      </Modal.Header>

      <Modal.Body className="py-4">
        {settingsReady ? (
          <>
          {/* 網路類型 */}
          <div className="mb-4">
            <Form.Label className="fw-bold">{t('network_type')}</Form.Label>
            <div
              className="p-3 rounded-pill text-center bg-primary text-white cursor-p"
            >
              <h6 className="mb-1">ETH (ERC20)</h6>
            </div>
          </div>

          {/* 收款錢包地址 */}
          <div className="mb-4">
            <Form.Label className="fw-bold">{t('wallet_address')}</Form.Label>
            <Form.Control
              type="text"
              placeholder={t('wallet_address_placeholder')}
              value={formData.walletAddress}
              onChange={(e) => handleInputChange('walletAddress', e.target.value)}
              isInvalid={!!errors.walletAddress}
            />
            {errors.walletAddress && (
              <div className="text-danger small mt-1">{errors.walletAddress}</div>
            )}
          </div>

          {/* 提領金額 */}
          <div className="mb-4">
            <Form.Label className="fw-bold">{t('withdraw_amount')}</Form.Label>
            <Form.Control
              type="number"
              placeholder="FCoin"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              isInvalid={!!errors.amount}
            />
            {errors.amount && (
              <div className="text-danger small mt-1">{errors.amount}</div>
            )}
          </div>

          {/* 提領資訊 */}
          <div className="mb-3">
            <div>
              <span className="">{t('available_balance')}</span>
              <span className="text-primary fw-bold fs-5">₣{balance} FCoin</span>
            </div>
            <div className="text-gray">
              <span>{t('minimum_withdraw_amount')}₣{settings.minWithdrawalAmount} FCoin</span>
            </div>
          </div>

          <hr />

          {/* 實際到帳 */}
          <div>
            <div>
              <span className="">{t('actual_amount')}</span>
              <span className="text-primary fw-bold fs-5">{calculateActualAmount().toFixed(2)} USDT</span>
            </div>
            <div>
              <span className="text-gray">{t('estimated_fee')}{calculateFee().toFixed(2)} USDT</span>
            </div>
          </div>
          </>
        ) : (
          <div className="text-center py-5">
            <span className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('loading')}</span>
            </span>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 d-flex justify-content-end gap-2">
        <button
          className="btn btn-neutral rounded-pill"
          onClick={handleClose}
        >
          {t('cancel')}
        </button>
        <button
          className="btn btn-primaryF rounded-pill"
          onClick={handleConfirm}
          disabled={!isValid()}
        >
          {t('confirm')}
        </button>
      </Modal.Footer>
    </Modal>
  );
});

WithdrawModal.displayName = 'WithdrawModal';

export default WithdrawModal;
